import { useComposedRefs } from "../../utils/use-composed-refs.js";
import { useDestroy } from "../../utils/use-destroy.js";
import { renderElement } from "../../utils/use-render.js";
import { MenuPopupContextProvider, useMenuContext } from "./context.js";
import { usePopupPosition } from "../popover/use-popup-position.js";
import { MenuPopupDataAttrs } from "@videojs/core";
import { MenuPositioningCSSVars, createMenuPopup, getRootPositionOptions } from "@videojs/core/dom";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
//#region src/ui/menu/popup.tsx
/** Positioned menu surface that contains one or more sibling Content pages. */
const MenuPopup = forwardRef(function MenuPopup({ render, className, style, onBlur, keepMounted = false, ...elementProps }, forwardedRef) {
	const { core, menu, parent, state, preferredSide, setPositionedSide, anchorName, boundary, container } = useMenuContext();
	if (parent) throw new Error("Menu.Popup must be a direct child of a root Menu.Root");
	const [popup] = useState(createMenuPopup);
	const [popupElement, setPopupElement] = useState(null);
	const internalRef = useRef(null);
	const setInternalElement = useCallback((element) => {
		menu.setPopupElement(element);
		popup.setElement(element);
		setPopupElement(element);
	}, [menu, popup]);
	const composedRef = useComposedRefs(forwardedRef, internalRef, setInternalElement);
	const positionOptions = useMemo(() => getRootPositionOptions(preferredSide, state.align), [preferredSide, state.align]);
	const positioningStyle = usePopupPosition({
		open: state.open,
		anchorName,
		position: positionOptions,
		triggerSource: menu,
		popupRef: internalRef,
		boundary,
		container,
		cssVars: MenuPositioningCSSVars,
		trackResize: false,
		onSideChange: setPositionedSide
	});
	useDestroy(popup);
	const inactive = !state.open && state.status !== "ending";
	if (inactive && !keepMounted) return null;
	const popupNode = renderElement("div", {
		render,
		className,
		style
	}, {
		state,
		stateAttrMap: MenuPopupDataAttrs,
		ref: composedRef,
		props: [
			{
				style: positioningStyle,
				...core.getPopupAttrs()
			},
			{ onBlur: (event) => {
				onBlur?.(event);
				menu.contentProps.onFocusOut(event);
			} },
			elementProps,
			{
				hidden: inactive,
				inert: inactive || void 0
			}
		]
	});
	return /* @__PURE__ */ jsx(MenuPopupContextProvider, {
		value: {
			popup,
			element: popupElement
		},
		children: popupNode
	});
});
//#endregion
export { MenuPopup };

//# sourceMappingURL=popup.js.map